import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Landmark, Plus, Loader2, Download, Settings, Upload, X, Image as ImageIcon,
  ChevronLeft, ChevronRight, Filter, Send, Mail, CreditCard, Calendar, Search,
  Wallet, ArrowLeftRight, Banknote, Layers, Eye, Pencil, Trash2, AlertTriangle, MailCheck
} from "lucide-react";

const FORMAS_PAGO = ["Tarjeta de Crédito/Débito", "Transferencia", "Efectivo"];

// Colores corporativos QAsesores (azul marino + verde) y acentos
const CARD_CONFIG = [
  { key: "all", label: "Total General", icon: Layers, color: "#0C314C" },
  { key: "Tarjeta de Crédito/Débito", label: "Tarjeta Créd./Déb.", icon: CreditCard, color: "#2A6F97" },
  { key: "Transferencia", label: "Transferencia", icon: ArrowLeftRight, color: "#4F8A2E" },
  { key: "Efectivo", label: "Efectivo", icon: Banknote, color: "#C9851A" },
];
const DIAS_SEMANA = [
  { v: 0, l: "Lun" }, { v: 1, l: "Mar" }, { v: 2, l: "Mié" },
  { v: 3, l: "Jue" }, { v: 4, l: "Vie" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];

const EMPTY_FORM = { nombre: "", apellido: "", cedula: "", concepto: "", monto: "", forma_pago: "" };
const EMPTY_FILTERS = { nombre: "", apellido: "", cedula: "", concepto: "", forma_pago: "all", ejecutiva: "", fecha: "", fecha_from: "", fecha_to: "" };

export default function LionBridgeCuadreCajaPage() {
  const { getAuthHeaders, user } = useAuth();
  const [transacciones, setTransacciones] = useState([]);
  const [totales, setTotales] = useState({ general: 0, por_forma_pago: {}, cantidad: 0 });
  const [isAdmin, setIsAdmin] = useState(false);
  const [soloLectura, setSoloLectura] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 30;

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [reporteEnviado, setReporteEnviado] = useState(false);
  const [enviandoReporte, setEnviandoReporte] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFiles, setImageFiles] = useState([]);

  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({ destinatarios: [], hora: "18:00", dias: [0, 1, 2, 3, 4], activo: false });
  const [nuevoCorreo, setNuevoCorreo] = useState("");
  const [savingConfig, setSavingConfig] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  const [viewImages, setViewImages] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [editTrans, setEditTrans] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTrans, setDeleteTrans] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const buildParams = useCallback((f) => {
    const p = new URLSearchParams();
    if (f.nombre) p.append("nombre", f.nombre);
    if (f.apellido) p.append("apellido", f.apellido);
    if (f.cedula) p.append("cedula", f.cedula);
    if (f.concepto) p.append("concepto", f.concepto);
    if (f.forma_pago && f.forma_pago !== "all") p.append("forma_pago", f.forma_pago);
    if (f.ejecutiva) p.append("ejecutiva", f.ejecutiva);
    if (f.fecha) p.append("fecha", f.fecha);
    if (f.fecha_from) p.append("fecha_from", f.fecha_from);
    if (f.fecha_to) p.append("fecha_to", f.fecha_to);
    return p;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = buildParams(appliedFilters);
      p.append("page", page);
      p.append("limit", limit);
      const res = await axios.get(`${API}/lionbridge/transacciones?${p.toString()}`, getAuthHeaders());
      setTransacciones(res.data.transacciones || []);
      setTotal(res.data.total || 0);
      setTotales(res.data.totales || { general: 0, por_forma_pago: {}, cantidad: 0 });
      setIsAdmin(res.data.is_admin || false);
      setSoloLectura(!!res.data.solo_lectura);
      setReporteEnviado(!!res.data.reporte_enviado_hoy);
    } catch (err) {
      toast.error("Error al cargar transacciones");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, appliedFilters, buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filtros en tiempo real: a medida que se escribe, la grilla filtra (debounce).
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setAppliedFilters(filters);
    }, 400);
    return () => clearTimeout(t);
  }, [filters]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lionbridge/config-envio`, getAuthHeaders());
      setConfig({
        destinatarios: res.data.destinatarios || [],
        hora: res.data.hora || "18:00",
        dias: res.data.dias || [0, 1, 2, 3, 4],
        activo: !!res.data.activo,
      });
    } catch (err) { /* solo admin */ }
  }, [getAuthHeaders]);

  const handleCreate = async () => {
    if (!form.nombre || !form.apellido || !form.cedula || !form.concepto || !form.monto || !form.forma_pago) {
      toast.error("Completa todos los campos");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/lionbridge/transacciones`, {
        ...form, monto: parseFloat(form.monto),
      }, getAuthHeaders());
      const trId = res.data.id;
      if (imageFiles.length > 0) {
        const fd = new FormData();
        imageFiles.forEach((f) => fd.append("files", f));
        await axios.post(`${API}/lionbridge/transacciones/${trId}/imagenes`, fd, {
          ...getAuthHeaders(),
          headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Transacción registrada");
      setShowDialog(false);
      setForm(EMPTY_FORM);
      setImageFiles([]);
      setPage(0);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const p = buildParams(appliedFilters);
      const res = await axios.get(`${API}/lionbridge/exportar?${p.toString()}`, {
        ...getAuthHeaders(), responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `cuadre_caja_${appliedFilters.fecha || new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exportación completada");
    } catch (err) {
      toast.error("Error al exportar");
    }
  };

  const handleEnviarReporte = async () => {
    setEnviandoReporte(true);
    try {
      const res = await axios.post(`${API}/lionbridge/enviar-reporte`, {}, getAuthHeaders());
      toast.success(res.data.message || "Reporte enviado");
      setReporteEnviado(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar el reporte");
    } finally {
      setEnviandoReporte(false);
    }
  };

  const openConfig = () => { fetchConfig(); setShowConfig(true); };

  const addCorreo = () => {
    if (!nuevoCorreo || !nuevoCorreo.includes("@")) { toast.error("Correo no válido"); return; }
    if (config.destinatarios.includes(nuevoCorreo)) return;
    setConfig({ ...config, destinatarios: [...config.destinatarios, nuevoCorreo] });
    setNuevoCorreo("");
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/lionbridge/config-envio`, config, getAuthHeaders());
      toast.success("Configuración guardada");
      setShowConfig(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSavingConfig(false); }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const res = await axios.post(`${API}/lionbridge/config-envio/enviar-ahora`, {}, getAuthHeaders());
      toast.success(res.data.message || "Enviado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar");
    } finally { setSendingNow(false); }
  };

  const applyFilters = () => { setPage(0); setAppliedFilters(filters); };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS); setPage(0); };

  // Click en tarjeta → filtra la grilla por forma de pago (o muestra todo)
  const selectForma = (key) => {
    const val = key === "all" ? "all" : key;
    const next = { ...appliedFilters, forma_pago: val };
    setFilters({ ...filters, forma_pago: val });
    setAppliedFilters(next);
    setPage(0);
  };
  const isCardActive = (key) => {
    const fp = appliedFilters.forma_pago;
    return key === "all" ? (!fp || fp === "all") : fp === key;
  };

  const fmt = (v) => `RD$ ${(v || 0).toLocaleString("es-DO", { minimumFractionDigits: 2 })}`;
  const formaColor = (fp) => (CARD_CONFIG.find((c) => c.key === fp)?.color) || "#64748B";

  const openEdit = (t) => {
    setEditTrans(t);
    setEditForm({ nombre: t.nombre, apellido: t.apellido, cedula: t.cedula, concepto: t.concepto, monto: String(t.monto), forma_pago: t.forma_pago });
  };
  const handleEdit = async () => {
    if (!editForm.nombre || !editForm.apellido || !editForm.cedula || !editForm.concepto || !editForm.monto || !editForm.forma_pago) {
      toast.error("Completa todos los campos"); return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/lionbridge/transacciones/${editTrans.id}`, { ...editForm, monto: parseFloat(editForm.monto) }, getAuthHeaders());
      toast.success("Transacción actualizada");
      setEditTrans(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally { setSavingEdit(false); }
  };
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/lionbridge/transacciones/${deleteTrans.id}`, getAuthHeaders());
      toast.success("Transacción eliminada");
      setDeleteTrans(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally { setDeleting(false); }
  };
  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="lionbridge-cuadre-page">
        <div className="flex flex-wrap justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold dark:text-white flex items-center gap-2">
              <Landmark className="w-7 h-7 text-[#0C314C]" /> Cuadre de Caja
            </h1>
            <p className="text-slate-500">Proyecto LionBridge — registro de transacciones del día</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            {isAdmin && (
              <Button variant="outline" onClick={openConfig} data-testid="config-envio-btn">
                <Settings className="w-4 h-4 mr-2" />Programar Envíos
              </Button>
            )}
            {!soloLectura && (
              <Button
                variant="outline"
                onClick={handleEnviarReporte}
                disabled={enviandoReporte || reporteEnviado}
                className={reporteEnviado ? "border-green-500 text-green-600" : ""}
                data-testid="enviar-reporte-btn"
              >
                {enviandoReporte ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : reporteEnviado ? <MailCheck className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {reporteEnviado ? "Reporte enviado" : "Enviar reporte"}
              </Button>
            )}
            <Button variant="outline" onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />Exportar Excel
            </Button>
            {!soloLectura && (
              <Button onClick={() => setShowDialog(true)} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="nueva-transaccion-btn">
                <Plus className="w-4 h-4 mr-2" />Nueva Transacción
              </Button>
            )}
          </div>
        </div>

        {/* Totales (tarjetas dinámicas: clic = filtrar la grilla) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {CARD_CONFIG.map((c) => {
            const Icon = c.icon;
            const active = isCardActive(c.key);
            const valor = c.key === "all" ? totales.general : (totales.por_forma_pago?.[c.key] || 0);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => selectForma(c.key)}
                data-testid={`card-${c.key === "all" ? "total-general" : "forma-" + c.key.split(" ")[0].toLowerCase()}`}
                className={`group relative text-left rounded-2xl p-5 overflow-hidden transition-all duration-300 focus:outline-none ${active ? "ring-4 ring-offset-2 dark:ring-offset-slate-900 shadow-xl -translate-y-0.5" : "shadow-md hover:-translate-y-1 hover:shadow-xl"}`}
                style={{ backgroundColor: c.color, ["--tw-ring-color"]: c.color }}
              >
                <div className="absolute -right-4 -bottom-4 opacity-15 group-hover:opacity-25 transition-opacity">
                  <Icon className="w-24 h-24 text-white" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 text-white/85 text-sm font-medium">
                    <Icon className="w-4 h-4" />{c.label}
                  </div>
                  <p className="text-2xl font-bold text-white mt-2" data-testid={c.key === "all" ? "total-general" : undefined}>
                    {fmt(valor)}
                  </p>
                  {c.key === "all" ? (
                    <p className="text-xs text-white/70 mt-1">{totales.cantidad} transacción(es)</p>
                  ) : (
                    <p className="text-xs text-white/70 mt-1">{active ? "Filtrando ✓" : "Clic para filtrar"}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Filtros */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-600 dark:text-slate-300"><Filter className="w-4 h-4" />Filtros</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input placeholder="Nombre" value={filters.nombre} onChange={(e) => setFilters({ ...filters, nombre: e.target.value })} data-testid="filter-nombre" />
              <Input placeholder="Apellido" value={filters.apellido} onChange={(e) => setFilters({ ...filters, apellido: e.target.value })} data-testid="filter-apellido" />
              <Input placeholder="Cédula" value={filters.cedula} onChange={(e) => setFilters({ ...filters, cedula: e.target.value })} data-testid="filter-cedula" />
              <Input placeholder="Concepto" value={filters.concepto} onChange={(e) => setFilters({ ...filters, concepto: e.target.value })} data-testid="filter-concepto" />
              <Select value={filters.forma_pago} onValueChange={(v) => setFilters({ ...filters, forma_pago: v })}>
                <SelectTrigger data-testid="filter-forma-pago"><SelectValue placeholder="Forma de pago" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las formas</SelectItem>
                  {FORMAS_PAGO.map((fp) => <SelectItem key={fp} value={fp}>{fp}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Ejecutiva" value={filters.ejecutiva} onChange={(e) => setFilters({ ...filters, ejecutiva: e.target.value })} data-testid="filter-ejecutiva" />
              <div><Label className="text-xs text-slate-500">Fecha desde</Label><Input type="date" value={filters.fecha_from} onChange={(e) => setFilters({ ...filters, fecha_from: e.target.value })} data-testid="filter-fecha-from" /></div>
              <div><Label className="text-xs text-slate-500">Fecha hasta</Label><Input type="date" value={filters.fecha_to} onChange={(e) => setFilters({ ...filters, fecha_to: e.target.value })} data-testid="filter-fecha-to" /></div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={applyFilters} data-testid="apply-filters-btn"><Search className="w-4 h-4 mr-2" />Buscar</Button>
              <Button size="sm" variant="ghost" onClick={clearFilters}>Limpiar</Button>
            </div>
          </CardContent>
        </Card>

        {/* Grilla */}
        <Card className="overflow-hidden border-slate-200 dark:border-slate-700 shadow-md">
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : transacciones.length === 0 ? (
              <div className="text-center py-16 text-slate-500">No hay transacciones para mostrar.</div>
            ) : (
              <table className="w-full min-w-[1000px] text-sm border-collapse" data-testid="transacciones-table">
                <thead>
                  <tr className="bg-gradient-to-r from-[#0C314C] to-[#2a4d7a] text-white">
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">No.</th>
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">Nombre</th>
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">Apellido</th>
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">Cédula</th>
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">Concepto</th>
                    <th className="px-4 py-3.5 text-right font-semibold uppercase tracking-wider text-xs">Monto</th>
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">Forma de Pago</th>
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">Fecha</th>
                    <th className="px-4 py-3.5 text-left font-semibold uppercase tracking-wider text-xs">Ejecutiva</th>
                    <th className="px-4 py-3.5 text-center font-semibold uppercase tracking-wider text-xs">Soporte</th>
                    {isAdmin && <th className="px-4 py-3.5 text-center font-semibold uppercase tracking-wider text-xs">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {transacciones.map((t, idx) => (
                    <tr
                      key={t.id}
                      className={`transition-colors border-b border-slate-100 dark:border-slate-700/60 ${idx % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50/70 dark:bg-slate-800/40"} hover:bg-[#0C314C]/5 dark:hover:bg-slate-700/60`}
                      data-testid={`row-${t.consecutivo}`}
                    >
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-[#0C314C]/10 text-[#0C314C] dark:bg-indigo-400/20 dark:text-indigo-200 font-bold text-xs">{t.consecutivo}</span>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-100">{t.nombre}</td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-200">{t.apellido}</td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 tabular-nums">{t.cedula}</td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-200">{t.concepto}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#0C314C] dark:text-emerald-300 tabular-nums">{fmt(t.monto)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: formaColor(t.forma_pago) }}>
                          {t.forma_pago}
                        </span>
                      </td>
                      <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300"><Calendar className="w-3.5 h-3.5 text-slate-400" />{t.fecha}</span></td>
                      <td className="px-4 py-3 text-slate-700 dark:text-slate-200">{t.ejecutiva}</td>
                      <td className="px-4 py-3 text-center">
                        {t.imagenes && t.imagenes.length > 0 ? (
                          <button className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[#2A6F97] hover:bg-[#2A6F97]/10 font-medium" onClick={() => setViewImages(t)} data-testid={`view-images-${t.consecutivo}`}>
                            <ImageIcon className="w-4 h-4" />{t.imagenes.length}
                          </button>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setViewDetail(t)} title="Ver" className="p-1.5 rounded-md text-slate-500 hover:text-[#0C314C] hover:bg-[#0C314C]/10" data-testid={`accion-ver-${t.consecutivo}`}><Eye className="w-4 h-4" /></button>
                            <button onClick={() => openEdit(t)} title="Editar" className="p-1.5 rounded-md text-slate-500 hover:text-[#2A6F97] hover:bg-[#2A6F97]/10" data-testid={`accion-editar-${t.consecutivo}`}><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => setDeleteTrans(t)} title="Borrar" className="p-1.5 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30" data-testid={`accion-borrar-${t.consecutivo}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0C314C]/5 dark:bg-slate-800 border-t-2 border-[#0C314C]/20">
                    <td colSpan={5} className="px-4 py-3 text-right font-semibold text-slate-600 dark:text-slate-300 uppercase text-xs tracking-wide">Total en pantalla</td>
                    <td className="px-4 py-3 text-right font-extrabold text-[#0C314C] dark:text-emerald-300 tabular-nums">{fmt(totales.general)}</td>
                    <td colSpan={isAdmin ? 5 : 4} className="px-4 py-3 text-slate-400 text-xs">{totales.cantidad} transacción(es)</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Paginación */}
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">{total} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm">{page + 1} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Dialog Nueva Transacción */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nueva Transacción</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="form-nombre" /></div>
              <div><Label>Apellido</Label><Input value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} data-testid="form-apellido" /></div>
              <div><Label>Cédula</Label><Input value={form.cedula} onChange={(e) => setForm({ ...form, cedula: e.target.value })} data-testid="form-cedula" /></div>
              <div><Label>Monto (RD$)</Label><Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} data-testid="form-monto" /></div>
              <div className="sm:col-span-2"><Label>Concepto</Label><Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} data-testid="form-concepto" /></div>
              <div className="sm:col-span-2">
                <Label>Forma de Pago</Label>
                <Select value={form.forma_pago} onValueChange={(v) => setForm({ ...form, forma_pago: v })}>
                  <SelectTrigger data-testid="form-forma-pago"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                  <SelectContent>{FORMAS_PAGO.map((fp) => <SelectItem key={fp} value={fp}>{fp}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Imágenes de soporte (opcional)</Label>
                <label className="mt-1 flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 text-sm text-slate-600 dark:text-slate-300">
                  <Upload className="w-4 h-4" /> Seleccionar imágenes
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setImageFiles(Array.from(e.target.files || []))} data-testid="form-imagenes" />
                </label>
                {imageFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {imageFiles.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs">
                        {f.name}
                        <button onClick={() => setImageFiles(imageFiles.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-1">La fecha y la ejecutiva se asignan automáticamente.</div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="save-transaccion-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Config Envío */}
        <Dialog open={showConfig} onOpenChange={setShowConfig}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" />Programar Envío del Cuadre</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="activo" checked={config.activo} onChange={(e) => setConfig({ ...config, activo: e.target.checked })} data-testid="config-activo" />
                <Label htmlFor="activo">Activar envío automático diario</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Hora (RD)</Label><Input type="time" value={config.hora} onChange={(e) => setConfig({ ...config, hora: e.target.value })} data-testid="config-hora" /></div>
              </div>
              <div>
                <Label>Días de envío</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {DIAS_SEMANA.map((d) => {
                    const active = config.dias.includes(d.v);
                    return (
                      <button key={d.v} type="button"
                        onClick={() => setConfig({ ...config, dias: active ? config.dias.filter((x) => x !== d.v) : [...config.dias, d.v] })}
                        className={`px-3 py-1 rounded-full text-sm border ${active ? "bg-[#0C314C] text-white border-[#0C314C]" : "border-slate-300 text-slate-600"}`}
                        data-testid={`config-dia-${d.v}`}>{d.l}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Destinatarios</Label>
                <div className="flex gap-2 mt-1">
                  <Input placeholder="correo@empresa.com" value={nuevoCorreo} onChange={(e) => setNuevoCorreo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCorreo()} data-testid="config-nuevo-correo" />
                  <Button type="button" variant="outline" onClick={addCorreo}>Agregar</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {config.destinatarios.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-xs">
                      {c}<button onClick={() => setConfig({ ...config, destinatarios: config.destinatarios.filter((x) => x !== c) })}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {config.destinatarios.length === 0 && <span className="text-xs text-slate-400">Sin destinatarios</span>}
                </div>
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={sendNow} disabled={sendingNow || config.destinatarios.length === 0} data-testid="enviar-ahora-btn">
                {sendingNow ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}Enviar cuadre de hoy ahora
              </Button>
              <Button onClick={saveConfig} disabled={savingConfig} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="save-config-btn">
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Guardar Configuración
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Ver Imágenes */}
        <Dialog open={!!viewImages} onOpenChange={(o) => !o && setViewImages(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Soportes — Transacción #{viewImages?.consecutivo}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
              {viewImages?.imagenes?.map((img) => (
                <a key={img.id} href={`${API}${img.url}`} target="_blank" rel="noreferrer">
                  <img src={`${API}${img.url}`} alt={img.filename} className="rounded-lg border w-full object-cover" data-testid={`soporte-img-${img.id}`} />
                </a>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Ver Detalle (admin) */}
        <Dialog open={!!viewDetail} onOpenChange={(o) => !o && setViewDetail(null)}>
          <DialogContent className="max-w-lg" data-testid="detalle-dialog">
            <DialogHeader><DialogTitle>Detalle — Transacción #{viewDetail?.consecutivo}</DialogTitle></DialogHeader>
            {viewDetail && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-400 block text-xs">Nombre</span>{viewDetail.nombre}</div>
                  <div><span className="text-slate-400 block text-xs">Apellido</span>{viewDetail.apellido}</div>
                  <div><span className="text-slate-400 block text-xs">Cédula</span>{viewDetail.cedula}</div>
                  <div><span className="text-slate-400 block text-xs">Monto</span><span className="font-bold text-[#0C314C]">{fmt(viewDetail.monto)}</span></div>
                  <div className="col-span-2"><span className="text-slate-400 block text-xs">Concepto</span>{viewDetail.concepto}</div>
                  <div><span className="text-slate-400 block text-xs">Forma de Pago</span><span className="inline-flex px-2 py-0.5 rounded-full text-xs text-white" style={{ backgroundColor: formaColor(viewDetail.forma_pago) }}>{viewDetail.forma_pago}</span></div>
                  <div><span className="text-slate-400 block text-xs">Fecha</span>{viewDetail.fecha}</div>
                  <div><span className="text-slate-400 block text-xs">Ejecutiva</span>{viewDetail.ejecutiva}</div>
                </div>
                <div>
                  <span className="text-slate-400 block text-xs mb-1">Soportes ({viewDetail.imagenes?.length || 0})</span>
                  {viewDetail.imagenes && viewDetail.imagenes.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                      {viewDetail.imagenes.map((img) => (
                        <a key={img.id} href={`${API}${img.url}`} target="_blank" rel="noreferrer">
                          <img src={`${API}${img.url}`} alt={img.filename} className="rounded-md border w-full h-24 object-cover" />
                        </a>
                      ))}
                    </div>
                  ) : <p className="text-sm text-slate-400">Sin imágenes de soporte</p>}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog Editar (admin) */}
        <Dialog open={!!editTrans} onOpenChange={(o) => !o && setEditTrans(null)}>
          <DialogContent className="max-w-lg" data-testid="editar-dialog">
            <DialogHeader><DialogTitle>Editar Transacción #{editTrans?.consecutivo}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nombre</Label><Input value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} data-testid="edit-nombre" /></div>
              <div><Label>Apellido</Label><Input value={editForm.apellido} onChange={(e) => setEditForm({ ...editForm, apellido: e.target.value })} data-testid="edit-apellido" /></div>
              <div><Label>Cédula</Label><Input value={editForm.cedula} onChange={(e) => setEditForm({ ...editForm, cedula: e.target.value })} data-testid="edit-cedula" /></div>
              <div><Label>Monto (RD$)</Label><Input type="number" step="0.01" value={editForm.monto} onChange={(e) => setEditForm({ ...editForm, monto: e.target.value })} data-testid="edit-monto" /></div>
              <div className="sm:col-span-2"><Label>Concepto</Label><Input value={editForm.concepto} onChange={(e) => setEditForm({ ...editForm, concepto: e.target.value })} data-testid="edit-concepto" /></div>
              <div className="sm:col-span-2">
                <Label>Forma de Pago</Label>
                <Select value={editForm.forma_pago} onValueChange={(v) => setEditForm({ ...editForm, forma_pago: v })}>
                  <SelectTrigger data-testid="edit-forma-pago"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                  <SelectContent>{FORMAS_PAGO.map((fp) => <SelectItem key={fp} value={fp}>{fp}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTrans(null)}>Cancelar</Button>
              <Button onClick={handleEdit} disabled={savingEdit} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="save-edit-btn">
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Borrar (admin) */}
        <Dialog open={!!deleteTrans} onOpenChange={(o) => !o && setDeleteTrans(null)}>
          <DialogContent className="max-w-md" data-testid="borrar-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" />Eliminar Transacción</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              ¿Seguro que deseas eliminar la transacción <b>#{deleteTrans?.consecutivo}</b> de <b>{deleteTrans?.nombre} {deleteTrans?.apellido}</b> por <b>{fmt(deleteTrans?.monto)}</b>? Esta acción no se puede deshacer.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTrans(null)}>Cancelar</Button>
              <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
